import { DefaultFontFaces, type Editor } from "tldraw";

/**
 * Sheet lettering: the one face the drafting register letters in, and the
 * measurements that go with it. Dims and callouts share these numbers rather
 * than each picking a font, because the thing a set is actually judged on is
 * lettering the same way on every sheet.
 *
 * The rules are Ching's (Architectural Graphics), which are also ASME Y14.2's
 * and ISO 3098's: single weight, sans serif, upright, uppercase, spaced by
 * area rather than by gap, with generous room between lines. The face only
 * has to not fight them, so it is a plain gothic chosen to hold up in both
 * media (Public Sans, drawn for documents that live on a screen and on
 * paper). The hand-lettered face stays where a hand belongs: freehand markup
 * and the user's own text.
 *
 * Sizes are screen pixels. Annotation graphics are screen-constant, so every
 * consumer multiplies by k (1/zoom) on its way to page units.
 */
export const SHEET_FONT = "var(--tl-font-sans)";
/** Exports have no CSS variables, so they name the embedded family. */
export const SHEET_FONT_EXPORT = "tldraw_sans, sans-serif";
export const SHEET_SIZE = 13;
/** Ching's leading: half a cap height of air between lines, at least. */
export const SHEET_LINE_HEIGHT = 1.5;
/** "Spaced by area, not by gap", in the only form CSS can say it. */
export const SHEET_TRACKING = 0.06;

/** The line pitch of a multi-line note, in screen pixels. */
export const SHEET_LEADING = SHEET_SIZE * SHEET_LINE_HEIGHT;

/** Rough ascent and descent of the face, as fractions of the size: what a
 *  box drawn around lettering (a hit area, a background mask) is hung from. */
export const SHEET_ASCENT = 0.82;
export const SHEET_DESCENT = 0.24;
/** How far a background mask clears the letters. */
export const SHEET_MASK_PAD = 3;

/**
 * The face itself. A shape util returns it from `getFontFaces` so tldraw
 * loads it, re-renders once it lands (which is what re-measures the text),
 * and embeds it in exports.
 */
export const SHEET_FONT_FACE = DefaultFontFaces.tldraw_sans.normal.normal;

/**
 * Lettering is uppercase on a sheet: it survives reproduction and a half-size
 * print, which is the legibility test a set has to pass. This is a rule about
 * how the sheet reads, not about what the note says, so it applies on the way
 * to the page and the stored text stays exactly as it was typed. Every path
 * that draws or measures sheet lettering goes through here, so the mask, the
 * hit box and the glyphs cannot disagree about what is being set.
 */
export function sheetCase(text: string): string {
  return text.toUpperCase();
}

export interface SheetLine {
  text: string;
  /** Offset from the block's top left, screen pixels. */
  x: number;
  y: number;
  w: number;
}

/**
 * The lettering broken into the lines it will be drawn as, wrapped at a
 * width. One DOM measurement for the whole note (tldraw measures every word
 * in a single pass), so laying a note out does not cost a measure per word.
 */
export function wrapSheetText(
  editor: Editor,
  text: string,
  width: number,
): SheetLine[] {
  const spans = editor.textMeasure.measureTextSpans(sheetCase(text), {
    overflow: "wrap",
    width,
    height: 0,
    padding: 0,
    fontFamily: SHEET_FONT,
    fontSize: SHEET_SIZE,
    fontStyle: "normal",
    fontWeight: "normal",
    lineHeight: SHEET_LINE_HEIGHT,
    textAlign: "start",
    otherStyles: { "letter-spacing": `${SHEET_TRACKING}em` },
  });
  const lines: SheetLine[] = [];
  for (const { text: span, box } of spans) {
    const line = lines.at(-1);
    if (!line || box.y > line.y) {
      lines.push({ text: span, x: box.x, y: box.y, w: box.w });
      continue;
    }
    line.text += span;
    // Trailing spaces sit past the last letter; they are not the line's
    // width, and a right-aligned block would hang on them.
    if (span.trim()) line.w = box.x + box.w - line.x;
  }
  return lines.map((line) => ({ ...line, text: line.text.trimEnd() }));
}

/**
 * How wide a run of sheet lettering renders, in screen pixels. Measured, not
 * estimated: the mask behind a note is drawn at this width, so a guess would
 * show. Explicit line breaks are preserved; the widest line wins.
 */
export function measureSheetText(editor: Editor, text: string): number {
  return editor.textMeasure.measureText(sheetCase(text), {
    fontFamily: SHEET_FONT,
    fontSize: SHEET_SIZE,
    fontStyle: "normal",
    fontWeight: "normal",
    lineHeight: SHEET_LINE_HEIGHT,
    maxWidth: null,
    padding: "0px",
    otherStyles: { "letter-spacing": `${SHEET_TRACKING}em` },
  }).w;
}
